import type { ComponentProps, ComponentType, KeyboardEvent } from 'react';
import { useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import KEYS from '@rover/constants/src/platform/keys.constants';
import PhotoGlyph from '@rover/icons/svg/photo.svg';
import { Box, Flex, Image } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import useTheme from '@rover/kibble/themes/useTheme';

import { GALLERY_IMAGE_SIZE } from './constants';

type KibbleImageProps = ComponentProps<typeof Image>;

/**
 * Kibble's `Image` hands any prop it does not recognise to the underlying
 * `<img>` (web) or RN `Image`, but types neither `onError` nor `fetchPriority`.
 */
type PassthroughImageProps = Omit<KibbleImageProps, 'webProps' | 'rxnProps'> & {
  webProps?: KibbleImageProps['webProps'] & {
    onError?: () => void;
    fetchPriority?: 'high' | 'low' | 'auto';
  };
  rxnProps?: KibbleImageProps['rxnProps'] & { onError?: () => void };
};

const PassthroughImage = Image as unknown as ComponentType<PassthroughImageProps>;

const FALLBACK_GLYPH_SIZE = 32;

type ImageWithShimmerProps = {
  src: string;
  caption?: string;
  /** Accessible name, taking precedence over the visible `caption`. */
  altText?: string;
  width?: number;
  height?: number;
  fluid?: boolean;
  onClick?: () => void;
  cached?: boolean;
  setCached?: (cached: boolean) => void;
  describedById?: string;
  /** Makes the tile a selection control, and says whether it is selected. */
  selected?: boolean;
  /** Accessible name for the press target, when the photo's own will not do. */
  pressLabel?: string;
  /**
   * Loads the image eagerly, at high fetch priority. Only for images that are
   * above the fold: everything else stays lazy.
   */
  priority?: boolean;
};
const ImageWithShimmer = ({
  src,
  caption,
  altText,
  width = parseInt(GALLERY_IMAGE_SIZE, 10),
  height = parseInt(GALLERY_IMAGE_SIZE, 10),
  fluid = false,
  onClick,
  cached,
  setCached,
  describedById,
  selected,
  pressLabel,
  priority = false,
}: ImageWithShimmerProps): JSX.Element => {
  const { t } = useLingui();
  const { textColor } = useTheme();
  const [loaded, setLoaded] = useState(cached ?? false);
  // Remembering which source failed, rather than that one did, lets a reused
  // instance — the lightbox pager swaps `src` in place — attempt the next photo.
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);
  const failed = failedSrc === src;
  const clickable = !!onClick;
  const selectable = selected !== undefined;
  const pressRole = selectable ? 'checkbox' : 'button';

  const fillSize = { width: '100%', height: '100%' };
  const boxSize = fluid ? fillSize : { width, height };
  const description = altText || caption || t`image`;

  return (
    <Box
      position="relative"
      {...boxSize}
      top="0"
      left="0"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...(fluid ? { aspectRatio: '1' } : {}),
      }}
    >
      <Box
        data-testid="open-light-box"
        onClick={onClick}
        role={clickable ? pressRole : undefined}
        webProps={{
          'aria-describedby': describedById,
          'aria-label': pressLabel,
          'aria-checked': selectable ? selected : undefined,
          tabIndex: clickable ? 0 : undefined,
          onKeyDown: clickable
            ? (e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === KEYS.ENTER || e.key === KEYS.SPACE) {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined,
        }}
        rxnProps={{
          accessibilityLabel: pressLabel,
          accessibilityState: selectable ? { checked: selected } : undefined,
        }}
        {...(fluid ? fillSize : {})}
        sx={{ cursor: clickable ? 'pointer' : 'default' }}
      >
        {failed ? (
          // Same box and radius as the photo it stands in for, so a broken
          // asset costs no layout shift. It keeps the photo's name, which is
          // what names the press target around it.
          <Flex
            testID="image-load-failed"
            {...boxSize}
            borderRadius="primary"
            background="tertiary"
            alignItems="center"
            justifyContent="center"
            webProps={{ role: 'img', 'aria-label': description }}
            rxnProps={{
              accessible: true,
              accessibilityRole: 'image',
              accessibilityLabel: description,
            }}
            sx={fluid ? fillSize : undefined}
          >
            <PhotoGlyph
              width={FALLBACK_GLYPH_SIZE}
              height={FALLBACK_GLYPH_SIZE}
              fill={textColor.secondary}
            />
          </Flex>
        ) : (
          <PassthroughImage
            src={src}
            description={description}
            resizeMethod={fluid ? 'cover' : 'contain'}
            {...boxSize}
            borderRadius="primary"
            sx={fluid ? fillSize : undefined}
            onLoad={() => {
              setLoaded(true);
              if (setCached) setCached(true);
            }}
            webProps={{
              eager: priority,
              fetchPriority: priority ? 'high' : undefined,
              onError: () => setFailedSrc(src),
            }}
            rxnProps={{ onError: () => setFailedSrc(src) }}
          />
        )}
      </Box>

      {!loaded && !failed && (
        <Box
          {...boxSize}
          position="absolute"
          top="0"
          left="0"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingShimmer
            {...boxSize}
            borderRadius="primary"
            m="0x"
            data-testid="loading-shimmer"
          />
        </Box>
      )}
    </Box>
  );
};

export default ImageWithShimmer;
